import React, { ComponentProps } from 'react';
import styled from '@emotion/styled';
import PropTypes, { InferProps } from 'prop-types';
import {
  EmailShareButton,
  EmailIcon,
  TwitterShareButton,
  TwitterIcon,
  FacebookShareButton,
  FacebookIcon,
  LinkedinShareButton,
  LinkedinIcon,
  PinterestShareButton,
  PinterestIcon,
  WhatsappShareButton,
  WhatsappIcon,
  VKShareButton,
  VKIcon,
} from 'react-share';

/**
 * Union of all share button props, derived from react-share components.
 */
type ShareButtonProperties =
  & ComponentProps<typeof EmailShareButton>
  & ComponentProps<typeof TwitterShareButton>
  & ComponentProps<typeof FacebookShareButton>
  & ComponentProps<typeof LinkedinShareButton>
  & ComponentProps<typeof PinterestShareButton>
  & ComponentProps<typeof WhatsappShareButton>
  & ComponentProps<typeof VKShareButton>;

const SocialWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
  > button {
    flex: 0 0 30px;
    height: 30px;
    width: 30px;
    margin: 5px;
    display: inline-flex;
    justify-content: center;
    align-items: flex-end;
    cursor: pointer;
  }
`;

const validPlatforms = [
  'Email',
  'Facebook',
  'Linkedin',
  'Pinterest',
  'Twitter',
  'VK',
  'Whatsapp',
] as const;

export type StandardSocialPlatform = typeof validPlatforms[number];

const socialPropertyTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.oneOf(validPlatforms).isRequired,
    modalProps: PropTypes.shape({}),
    iconProps: PropTypes.shape({}),
    additionalProps: PropTypes.shape({}),
    className: PropTypes.string,
  })),
  className: PropTypes.string,
};

const shareButtons = {
  Email: EmailShareButton,
  Twitter: TwitterShareButton,
  Facebook: FacebookShareButton,
  Linkedin: LinkedinShareButton,
  Pinterest: PinterestShareButton,
  Whatsapp: WhatsappShareButton,
  VK: VKShareButton,
};

const icons = {
  Email: EmailIcon,
  Twitter: TwitterIcon,
  Facebook: FacebookIcon,
  Linkedin: LinkedinIcon,
  Pinterest: PinterestIcon,
  Whatsapp: WhatsappIcon,
  VK: VKIcon,
};

export type SocialProperties = Omit<InferProps<
  typeof socialPropertyTypes
>, 'data'> & {
  data?: {
    name: StandardSocialPlatform;
    modalProps: {
      title?: string;
      url: string;
      media?: string;
      hashtag?: string;
    };
    iconProps: {
      bgStyle?: { fill: string };
      iconFillColor?: string;
      size?: number | undefined;
    };
    additionalProps: {
      'aria-label': string;
    };
    className: string;
    locales?: string[];
    pages?: string[];
    checkMedia?: boolean;
  }[];
  className?: string;
};

const Social = ({ data, className }: SocialProperties) => {
  const result = (data ?? []).map((shareModal, index) => {
    const id = `share-${index}`;

    const Button = shareButtons[shareModal.name];
    const Icon = icons[shareModal.name];

    const buttonProperties = {
      ...shareModal.modalProps,
      ...shareModal.additionalProps,
      className: shareModal.className,
    } as ShareButtonProperties;

    return (
      <Button {...buttonProperties} key={id}>
        <Icon
          {...shareModal.iconProps}
          size={shareModal.iconProps.size ?? 30}
          round
          className={shareModal.className}
        />
      </Button>
    );
  });
  return (
    <SocialWrapper className={className}>
      {result}
    </SocialWrapper>
  );
};

Social.propTypes = socialPropertyTypes;

export default Social;
